import {
  SET_GLINTS_TRACKING_NAME,
  SET_NO_INDEX,
  UNSET_NO_INDEX,
  UPDATE_METADATA,
} from '../actions/meta';
import { COUNTRY, SupportedCountry } from '../common/constants';

type UpdateMetadataPayload = {
  title?: string;
  description?: string;
  image?: string;
  prerenderStatusCode?: number;
  canonical?: string;
  siteName?: string;
  skipTwitterSite?: boolean;
  glintsTrackingName?: string;
};

type MetaState = {
  title: string;
  description: string;
  image: string | null;
  prerenderStatusCode: number | null;
  noIndex: boolean;
  location: string | null;
  canonical: string | null;
  siteName?: string;
  skipTwitterSite?: boolean;
  glintsTrackingName?: string | null;
};

type MetaAction =
  | { type: typeof UPDATE_METADATA; payload: UpdateMetadataPayload }
  | { type: typeof SET_NO_INDEX }
  | { type: typeof UNSET_NO_INDEX }
  | { type: typeof SET_GLINTS_TRACKING_NAME; payload: string | null };

const defaultState: MetaState = {
  title: 'Glints - Career Discovery & Development Platform',
  description: `Explore internships, graduate jobs & career development resources in Singapore on Glints.
    Do what you love on our career discovery & development platform.`,
  image: '/images/logo-glints-square.png',
  prerenderStatusCode: null,
  noIndex: false,
  location: null,
  canonical: null,
};

const stateForCountry: Record<SupportedCountry, MetaState> = {
  default: defaultState,
  hk: defaultState,
  ph: defaultState,
  th: defaultState,
  tw: defaultState,
  vn: defaultState,
  id: {
    ...defaultState,
    description: `Explore internships, graduate jobs & career development resources in Indonesia on Glints.
      Do what you love on our career discovery & development platform.`,
    image: '/images/logo-glints-square-taploker.png',
  },
  my: {
    ...defaultState,
    description: `Explore internships, graduate jobs & career development resources in Malaysia on Glints.
      Do what you love on our career discovery & development platform.`,
    image: '/images/logo-glints-square-tapjobs.png',
  },
  sg: {
    ...defaultState,
    description: `Explore internships, graduate jobs & career development resources in Singapore on Glints.
      Do what you love on our career discovery & development platform.`,
    image: '/images/logo-glints-square-tapjobs.png',
  },
};

const initialState: MetaState =
  stateForCountry[COUNTRY] ?? stateForCountry.default;

const metaReducer = (
  state: MetaState = initialState,
  action: MetaAction
): MetaState => {
  switch (action.type) {
    case UPDATE_METADATA: {
      const { payload } = action;

      return {
        ...state,
        title: payload.title ? payload.title : initialState.title,
        description:
          payload.description === ''
            ? ''
            : payload.description ?? initialState.description,
        siteName: payload.siteName,
        skipTwitterSite: payload.skipTwitterSite,
        image: payload.image
          ? payload.image.startsWith('https:') ||
            payload.image.startsWith('http:')
            ? payload.image
            : `https:${payload.image}`
          : null,
        prerenderStatusCode: payload.prerenderStatusCode
          ? payload.prerenderStatusCode
          : null,
        location: typeof window === 'undefined' ? null : window.location.href,
        canonical: payload.canonical ?? null,
      };
    }
    case SET_NO_INDEX:
      return {
        ...state,
        noIndex: true,
      };
    case UNSET_NO_INDEX:
      return {
        ...state,
        noIndex: false,
      };
    case SET_GLINTS_TRACKING_NAME:
      return {
        ...state,
        glintsTrackingName: action.payload,
      };
    default:
      return state;
  }
};

export default metaReducer;
